import { useMemo } from 'react';

import type { EstimationModelResponse } from '@/api/schema/data-contracts';
import { useMessages } from '@/hooks/messages';
import type {
  CostEstimationItemsTableRecord,
  CostModel,
  ResourceEstimationItemsTableRecord,
} from '@/pages/estimation/detail/model';

import HierarchicalManagementReportTable, {
  type HierarchicalTableRow,
} from '../components/HierarchicalManagementReportTable';
import { managementReportSummaryTableMessageDefinitions } from './messages';

export interface ManagementReportSummaryTableProps {
  resourceEstimationItems: ResourceEstimationItemsTableRecord[];
  costEstimationItems: CostEstimationItemsTableRecord[];
  costs?: CostModel;
  estimationModel: EstimationModelResponse;
  getResourceEstimationItemsTableRecordsByParent: (
    parentId: string | undefined,
  ) => ResourceEstimationItemsTableRecord[];
  getCostEstimationItemsTableRecordsByParent: (
    parentId: string | undefined,
  ) => CostEstimationItemsTableRecord[];
  assumptions?: Array<{
    id: string;
    estimationItemId: string;
    description: string;
    isValid: boolean;
  }>;
  searchText?: string;
  hiddenCostTypeColumns?: string[];
}

const ManagementReportSummaryTable: React.FC<ManagementReportSummaryTableProps> = ({
  resourceEstimationItems = [],
  estimationModel,
  costs,
  getResourceEstimationItemsTableRecordsByParent,
  assumptions = [],
  searchText = '',
  hiddenCostTypeColumns = [],
}: ManagementReportSummaryTableProps) => {
  const messages = useMessages(managementReportSummaryTableMessageDefinitions);

  const tableConfig = useMemo(
    () => ({
      items: resourceEstimationItems,
      getChildrenByParent: getResourceEstimationItemsTableRecordsByParent,
      createRowFromItem: (
        item: ResourceEstimationItemsTableRecord,
        _counter: number,
        _subCounter?: number,
        level: number = 0,
      ) => ({
        id: `resource-${item.id}`,
        reference: item.reference,
        name: item.name,
        description: item.description,
        isParent: false,
        level,
        estimationItemId: item.id,
        resourceEstimationItem: item,
      }),
      getEstimationItemForVisibility: (row: HierarchicalTableRow) => row.resourceEstimationItem,
      defaultGroupName: 'Project Phase',
      showEffortsColumn: true,
      getEffortValue: (row: HierarchicalTableRow) =>
        row.resourceEstimationItem?.calculatedResult ?? 0,
      emptyStateMessage: messages.noDirectCostItems(),
    }),
    [resourceEstimationItems, getResourceEstimationItemsTableRecordsByParent, messages],
  );

  return (
    <HierarchicalManagementReportTable
      config={tableConfig}
      costs={costs}
      estimationModel={estimationModel}
      assumptions={assumptions}
      searchText={searchText}
      hiddenCostTypeColumns={hiddenCostTypeColumns}
    />
  );
};

export default ManagementReportSummaryTable;
